import React, { memo, useMemo } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import icProductSelected from "assets/svg/ic_product_selected.svg";
import icProductUnselected from "assets/svg/ic_product_unselected.svg";
import icCartSelected from "assets/svg/ic_cart_selected.svg";
import icCartUnselected from "assets/svg/ic_cart_unselected.svg";
import icOrderSessionSelected from "assets/svg/ic_order_session_selected.svg";
import icOrderSessionUnselected from "assets/svg/ic_order_session_unselected.svg";
import icPaymentSelected from "assets/svg/ic_payment_selected.svg";
import icPaymentUnselected from "assets/svg/ic_payment_unselected.svg";

const NAV_ITEMS = [
  {
    key: "products",
    labelKey: "orderDish",
    path: "/products",
    selectedIcon: icProductSelected,
    unselectedIcon: icProductUnselected,
  },
  {
    key: "cart",
    path: "/cart",
    selectedIcon: icCartSelected,
    unselectedIcon: icCartUnselected,
  },
  {
    key: "payment",
    labelKey: "orderedDish",
    path: "/ordered",
    selectedIcon: icOrderSessionSelected,
    unselectedIcon: icOrderSessionUnselected,
  },
  {
    key: "bill",
    labelKey: "payment",
    path: "/checkout",
    selectedIcon: icPaymentSelected,
    unselectedIcon: icPaymentUnselected,
  },
];

const VISIBLE_PATHS = new Set([
  "/products",
  "/cart",
  "/ordered",
  "/checkout",
]);

const BottomNavigation = memo(({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const cartDetailTotal = useSelector(
    (state) => state.rootReducer.cartDetailTotal,
  );

  const isVisible = useMemo(
    () => VISIBLE_PATHS.has(location.pathname),
    [location.pathname],
  );

  if (!isVisible) return null;

  return (
    <div className={className}>
      <div className="bottom-nav-shell">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          const iconSrc = isActive ? item.selectedIcon : item.unselectedIcon;
          const label =
            item.key === "cart"
              ? t("cartLabel")
              : t(item.labelKey);

          return (
            <button
              key={item.key}
              type="button"
              className={`nav-item ${isActive ? "is-active" : ""} ${item.key === "cart" ? "cart-item" : ""}`}
              onClick={() => navigate(item.path)}
            >
              <div className="icon-wrap">
                <div className="icon-circle">
                  <img
                    src={iconSrc}
                    alt={label || item.key}
                    className="icon-image"
                  />
                </div>
                {item.key === "cart" && cartDetailTotal?.totalQuantity > 0 && (
                  <span className="badge">{cartDetailTotal.totalQuantity}</span>
                )}
              </div>
              {label ? <span className="label">{label}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default styled(BottomNavigation)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
  pointer-events: none;

  .bottom-nav-shell {
    pointer-events: auto;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: end;
    gap: 2px;
    min-height: 86px;
    padding: 8px 10px calc(env(safe-area-inset-bottom, 0px) + 10px);
    background: #ffffff;
    border-top: 1px solid #ececec;
    box-shadow: 0 -6px 18px rgba(26, 26, 26, 0.06);
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-height: 60px;
    padding: 0;
    border: none;
    background: transparent;
    color: #a8b3bc;
  }

  .icon-wrap {
    position: relative;
  }

  .icon-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }

  .icon-image {
    display: block;
    width: 24px;
    height: 24px;
  }

  .label {
    font-size: 13px;
    line-height: 18px;
    font-family: "Manrope-Medium", sans-serif;
    white-space: nowrap;
  }

  .is-active {
    color: #ef232b;
  }

  .cart-item.is-active .icon-image {
    filter: brightness(0) saturate(100%) invert(19%) sepia(95%) saturate(5246%)
      hue-rotate(351deg) brightness(92%) contrast(94%);
  }

  .badge {
    position: absolute;
    top: -8px;
    right: -9px;
    width: 22px;
    height: 22px;
    padding-top: 3px;
    border-radius: 999px;
    background: #f29a22;
    color: #ffffff;
    font-size: 10px;
    line-height: 10px;
    text-align: center;
    font-family: "Manrope-Bold", sans-serif;
    border: 3px solid #ffffff;
  }
`;
